// src/components/DashboardHeader.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Layout, Button, Space, Tag, Tooltip, Typography } from 'antd';
import { SettingOutlined, LogoutOutlined, MenuOutlined, BankOutlined, DollarOutlined, PhoneOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { TranslationOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import { useClinicContext } from './ClinicContext';
import { useOwnerContext } from './OwnerContext';
import API from '../config/api';

const { Header } = Layout;
const { Text } = Typography;
const CONTACT_PHONE = '+201063030622';
const CONTACT_TEL_URL = `tel:${CONTACT_PHONE}`;
const CONTACT_WHATSAPP_URL = 'https://wa.me/201063030622';

interface HeaderProps {
  onSettingsClick: () => void;
  onMenuClick?: () => void;
  isMobile?: boolean;
}

const DashboardHeader: React.FC<HeaderProps> = ({ onSettingsClick, onMenuClick, isMobile = false }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { selectedClinic } = useClinicContext();
  const { canSeeRevenueAnalytics, loading: ownerLoading } = useOwnerContext();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const clinicName = selectedClinic?.name || '';

  // ── Today's earnings (single source of truth: /api/revenue/doctor/:id/today) ──
  // Visible to anyone who has a doctorId scope in localStorage (assistants, doctors,
  // owners viewing a specific doctor). Polls every 30s + on window focus so the
  // assistant sees their الصافي for the day update as bills are created/refunded.
  const [todayRevenue, setTodayRevenue] = useState<number | null>(null);

  const fetchTodayRevenue = useCallback(async () => {
    if (ownerLoading || !canSeeRevenueAnalytics) {
      setTodayRevenue(null);
      return;
    }
    const doctorId = localStorage.getItem('doctorId');
    if (!doctorId) {
      setTodayRevenue(null);
      return;
    }
    try {
      const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.REVENUE_TODAY(doctorId)}`);
      if (res.data?.success) {
        setTodayRevenue(Number(res.data.data?.totalRevenue) || 0);
      }
    } catch {
      // Silent fail — keep last known value rather than flashing the indicator.
    }
  }, [canSeeRevenueAnalytics, ownerLoading]);

  useEffect(() => {
    fetchTodayRevenue();
    const intervalId = setInterval(fetchTodayRevenue, 30_000);
    const onFocus = () => fetchTodayRevenue();
    const onDoctorIdChanged = () => fetchTodayRevenue();
    window.addEventListener('focus', onFocus);
    window.addEventListener('doctorIdChanged', onDoctorIdChanged);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('doctorIdChanged', onDoctorIdChanged);
    };
  }, [fetchTodayRevenue]);

  const formattedTodayRevenue = todayRevenue !== null
    ? new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
        maximumFractionDigits: 0,
      }).format(todayRevenue)
    : null;
  const todayColor = todayRevenue === null
    ? 'default'
    : todayRevenue > 0 ? 'green' : todayRevenue < 0 ? 'red' : 'default';
  const contactLabel = language === 'ar' ? 'تواصل' : 'Contact';
  const callLabel = language === 'ar' ? 'اتصال' : 'Call';
  const whatsappLabel = language === 'ar' ? 'واتساب' : 'WhatsApp';


  return (
    <>
    <Header
      style={{
        padding: isMobile ? '0 8px' : '0 16px',
        background: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f0f0f0',
        gap: 8,
      }}
    >
      {/* Left side: menu button + clinic name */}
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1, gap: 8 }}>
        {isMobile && onMenuClick && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={onMenuClick}
            style={{ flexShrink: 0 }}
          />
        )}
        {clinicName && (
          <Tooltip title={clinicName} placement="bottomLeft">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <BankOutlined style={{ color: '#1890ff', flexShrink: 0, fontSize: isMobile ? 14 : 16 }} />
              <Text
                strong
                style={{
                  color: '#262626',
                  fontSize: isMobile ? 13 : 15,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isMobile ? 120 : 280,
                  display: 'block',
                }}
              >
                {clinicName}
              </Text>
            </div>
          </Tooltip>
        )}
      </div>

      {/* Daily earnings — MongoDB (single source of truth) */}
      {!ownerLoading && canSeeRevenueAnalytics && formattedTodayRevenue !== null && (
        <Tooltip title={`${t('todayEarnings')} (MongoDB)`}>
          <Tag
            color={todayColor}
            icon={<DollarOutlined />}
            style={{
              flexShrink: 0,
              fontWeight: 600,
              fontSize: isMobile ? 12 : 14,
              padding: isMobile ? '2px 6px' : '4px 10px',
              margin: 0,
            }}
          >
            {formattedTodayRevenue} {isMobile ? '' : 'EGP'}
          </Tag>
        </Tooltip>
      )}

      {/* Right side: action buttons — flex-shrink: 0 so they never get squeezed */}
      <Space style={{ flexShrink: 0 }}>
        <Tooltip title={`${whatsappLabel}: ${CONTACT_PHONE}`}>
          <Button
            type="text"
            href={CONTACT_WHATSAPP_URL}
            target="_blank"
            icon={<WhatsAppOutlined style={{ color: '#128c4a' }} />}
            style={isMobile ? { padding: '4px 6px' } : {}}
          >
            {isMobile ? '' : whatsappLabel}
          </Button>
        </Tooltip>
        <Tooltip title={`${contactLabel}: ${CONTACT_PHONE}`}>
          <Button
            type="text"
            href={CONTACT_TEL_URL}
            icon={<PhoneOutlined />}
            style={isMobile ? { padding: '4px 6px' } : {}}
          >
            {isMobile ? '' : callLabel}
          </Button>
        </Tooltip>
        <Button
          type="text"
          icon={<TranslationOutlined />}
          onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
          style={isMobile ? { padding: '4px 6px' } : {}}
        >
          {isMobile ? '' : (language === 'en' ? 'عربي' : 'English')}
        </Button>
        <Button
          type="text"
          icon={<SettingOutlined />}
          onClick={onSettingsClick}
          style={isMobile ? { padding: '4px 6px' } : {}}
        >
          {isMobile ? '' : t('settings')}
        </Button>
        <Button
          type="text"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          style={isMobile ? { padding: '4px 6px' } : {}}
        >
          {isMobile ? '' : t('logout')}
        </Button>
      </Space>
    </Header>
    </>
  );
};

export default DashboardHeader;
